import { getAuthToken, getDeviceId } from '../auth-session';
import { logger } from '../logger';
import { useActivityStore } from '../../store/activityStore';
import { API_BASE_URL, fetchWithRetry, getAuthHeaders, runInParallelPool } from './core';

const ACTIVITY_LOG_PAGE_SIZE = 50;
const ACTIVITY_LOG_MAX_PAGES = 5;
const DEVICE_ID_PATTERN = /^[a-zA-Z0-9._:-]+$/;

const normalizeActivityDeviceId = (value: unknown): string | undefined => {
  if (typeof value !== 'string') {
    return undefined;
  }
  const trimmed = value.trim();
  if (!trimmed || trimmed.length > 128) {
    return undefined;
  }
  if (!DEVICE_ID_PATTERN.test(trimmed)) {
    return undefined;
  }
  return trimmed;
};

/**
 * Syncs a single encrypted log to MongoDB via API ONE BY ONE.
 */
export const uploadActivityLog = async (log: any): Promise<boolean> => {
  if (!getAuthToken()) {
    return false;
  }
  try {
    const stableDeviceId =
      normalizeActivityDeviceId(getDeviceId()) || normalizeActivityDeviceId(log.userOrSessionId);
    const sessionName =
      typeof log.sessionName === 'string' && log.sessionName.trim()
        ? log.sessionName.trim()
        : undefined;

    await fetchWithRetry(
      `${API_BASE_URL}/api/vault/activity`,
      {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: log.actionType,
          category: 'system',
          itemId: log.itemName,
          ...(stableDeviceId ? { deviceId: stableDeviceId } : {}),
          status: log.status === 'success' ? 'SUCCESS' : 'FAILURE',
          details: {
            description: log.description,
            ...(sessionName ? { sessionName } : {}),
          },
        }),
      },
      'Activity Log Sync',
    );
    return true;
  } catch (error: any) {
    if (error?.status === 429) {
      throw error;
    }
    logger.error('Activity Log Sync failed', { error });
    return false;
  }
};

/**
 * Syncs an array of logs to MongoDB via API using bounded parallel execution.
 */
export const uploadLogsToCloud = async (logs: any[], concurrency = 4) => {
  if (!getAuthToken()) return;
  const { markAsSynced } = useActivityStore.getState();
  const pendingLogs = logs.filter((log) => log.isSynced === false || log.isSynced === undefined);
  if (pendingLogs.length === 0) return;

  const syncedIds: string[] = [];
  let isRateLimited = false;

  await runInParallelPool(
    pendingLogs,
    async (log) => {
      if (isRateLimited) return;
      try {
        const success = await uploadActivityLog(log);
        if (success) {
          syncedIds.push(log.id);
        }
      } catch (err: any) {
        if (err?.status === 429 || err?.code === 'TOO_MANY_REQUESTS') {
          isRateLimited = true;
        }
      }
    },
    concurrency,
  );

  if (syncedIds.length > 0) {
    markAsSynced(syncedIds);
  }
};

/**
 * Downloads and decrypts logs from MongoDB.
 */
export const downloadLogsFromCloud = async (): Promise<any[] | null> => {
  try {
    const logs: any[] = [];

    for (let page = 0; page < ACTIVITY_LOG_MAX_PAGES; page += 1) {
      const skip = page * ACTIVITY_LOG_PAGE_SIZE;
      const response = await fetchWithRetry(
        `${API_BASE_URL}/api/vault/activity?limit=${ACTIVITY_LOG_PAGE_SIZE}&skip=${skip}`,
        { method: 'GET', headers: getAuthHeaders() },
        'Logs Cloud Sync',
      );
      const body = await response.json();
      const data = Array.isArray(body) ? body : body?.data;
      if (!data || !Array.isArray(data)) {
        return logs.length > 0 ? logs : null;
      }

      logs.push(...data);

      if (data.length < ACTIVITY_LOG_PAGE_SIZE) {
        break;
      }
    }

    return logs.map((log: any) => ({
      id: log._id,
      actionType: log.action,
      itemName: log.itemId || log.details?.itemId || 'App',
      timestamp: Date.parse(log.timestamp || log.createdAt) || Date.now(),
      userOrSessionId: log.device || log.metadata?.deviceId || 'unknown',
      sessionName: log.details?.sessionName || log.device || log.metadata?.deviceId || 'unknown',
      status: log.status === 'SUCCESS' ? 'success' : 'failure',
      description: log.details?.description || '',
      isSynced: true,
    }));
  } catch (error: any) {
    if (error?.status === 429) {
      logger.warn('downloadLogsFromCloud paused: rate limit active');
      return null;
    }
    logger.error('downloadLogsFromCloud failed', { error });
    return null;
  }
};
